import React, { useContext, useState, useEffect } from "react";
import PageLayout from "../../../layouts/PageLayout";
import { Container, Row, Col, Button, Card, Spinner, Form, Dropdown } from "react-bootstrap";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import RemindersTable from "../../../components/Admin/RemindersTable";

// React Icons
import {
    FaBell, FaCalendarDays, FaHourglassHalf, FaFolderOpen,
    FaSliders, FaClock, FaLayerGroup, FaShieldHalved,
    FaUser, FaPaw, FaMagnifyingGlass, FaRotateLeft,
    FaChevronDown, FaCheck, FaCalendar
} from "react-icons/fa6";

const ReminderPage = () => {
    const translator = useContext(TranslatorContext);
    const translate = translator && typeof translator.translate === 'function'
        ? translator.translate
        : (text) => text;

    const [reminders, setReminders] = useState([]);
    const [stats, setStats] = useState(null);
    const [filtersData, setFiltersData] = useState(null);
    const [loading, setLoading] = useState(true);
    const Authtoken = localStorage.getItem("authToken");

    // Search states for dropdowns
    const [dateSearch, setDateSearch] = useState("");
    const [typeSearch, setTypeSearch] = useState("");
    const [statusSearch, setStatusSearch] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("");

    // Active query state
    const [activeFilters, setActiveFilters] = useState({
        filter: "all", // today, next_3_days, last_3_days, month
        type: "all",
        status: "all",
        searchBy: "name",
        searchQuery: "",
        petSearchBy: "name",
        petSearchQuery: "",
        pet_type: "all",
        month: "", // Store selected month in YYYY-MM format
        start_date: "",
        end_date: ""
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const token = localStorage.getItem("authToken");
                const headers = { Authorization: `Bearer ${token}` };

                const [dropdownRes, statsRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/reminders/dropdowns`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_URL}/reminders/stats`, { headers })
                ]);

                if (dropdownRes.data.success) setFiltersData(dropdownRes.data.data);
                if (statsRes.data.success) setStats(statsRes.data.data);
            } catch (error) {
                console.error("Error loading setup data:", error);
            }
        };
        fetchInitialData();
    }, []);

    // Function to get month start and end dates
    const getMonthRange = (monthYear) => {
        if (!monthYear) return null;
        const [year, month] = monthYear.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0);
        return {
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0]
        };
    };

    useEffect(() => {
        const fetchReminders = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("authToken");
                const params = {};

                // Handle date filters
                if (activeFilters.filter === "today") {
                    const today = new Date().toISOString().split('T')[0];
                    params.start_date = today;
                    params.end_date = today;
                } else if (activeFilters.filter === "next_3_days") {
                    const today = new Date();
                    const startDate = new Date(today);
                    startDate.setDate(today.getDate() + 1);
                    const endDate = new Date(today);
                    endDate.setDate(today.getDate() + 3);
                    params.start_date = startDate.toISOString().split('T')[0];
                    params.end_date = endDate.toISOString().split('T')[0];
                } else if (activeFilters.filter === "last_3_days") {
                    const today = new Date();
                    const endDate = new Date(today);
                    endDate.setDate(today.getDate() - 1);
                    const startDate = new Date(today);
                    startDate.setDate(today.getDate() - 3);
                    params.start_date = startDate.toISOString().split('T')[0];
                    params.end_date = endDate.toISOString().split('T')[0];
                } else if (activeFilters.filter === "month" && activeFilters.month) {
                    const range = getMonthRange(activeFilters.month);
                    if (range) {
                        params.start_date = range.start_date;
                        params.end_date = range.end_date;
                    }
                }

                if (activeFilters.type !== "all") params.type = activeFilters.type;
                if (activeFilters.status !== "all") params.status = activeFilters.status;
                if (activeFilters.pet_type !== "all") params.pet_type = activeFilters.pet_type;

                if (activeFilters.searchQuery) {
                    params[`customer_${activeFilters.searchBy}`] = activeFilters.searchQuery;
                }
                if (activeFilters.petSearchQuery) {
                    params[`pet_${activeFilters.petSearchBy}`] = activeFilters.petSearchQuery;
                }

                console.log('Sending params:', params);

                const response = await axios.get(`${process.env.REACT_APP_API_URL}/reminders`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params
                });

                if (response.data.success) {
                    setReminders(response.data.data);
                }
            } catch (error) {
                console.error("Error retrieving reminders:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchReminders();
    }, [activeFilters]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setActiveFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleDateFilterChange = (filterValue) => {
        if (filterValue === "month") {
            // If month is selected, keep the current month or set default
            const currentMonth = new Date().toISOString().slice(0, 7);
            setActiveFilters(prev => ({
                ...prev,
                filter: filterValue,
                month: prev.month || currentMonth
            }));
        } else {
            setActiveFilters(prev => ({
                ...prev,
                filter: filterValue,
                month: ""
            }));
        }
    };

    const handleMonthChange = (e) => {
        const month = e.target.value;
        setActiveFilters(prev => ({
            ...prev,
            month: month,
            filter: "month"
        }));
    };

    const handleClear = () => {
        setActiveFilters({
            filter: "all",
            type: "all",
            status: "all",
            searchBy: "name",
            searchQuery: "",
            petSearchBy: "name",
            petSearchQuery: "",
            pet_type: "all",
            month: "",
            start_date: "",
            end_date: ""
        });
        setDateSearch("");
        setTypeSearch("");
        setStatusSearch("");
        setSelectedMonth("");
    };

    // Filter options for date
    const dateFilterOptions = [
        { value: "all", label: "All Reminders" },
        { value: "today", label: "Today" },
        { value: "next_3_days", label: "Next 3 Days" },
        { value: "last_3_days", label: "Last 3 Days" },
    ];

    const selectedDateLabel = dateFilterOptions.find(d => d.value === activeFilters.filter)?.label || "Select Date Filter";

    return (
        <PageLayout>
            <style>{`
                .stat-card-advanced {
                    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
                    border: none !important;
                }
                .stat-card-advanced:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.08) !important;
                }
                .icon-shape-wrapper {
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                }
                .custom-search-dropdown .dropdown-toggle {
                    width: 100%;
                    text-align: left;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background-color: #fff !important;
                    color: #495057 !important;
                    border: 1px solid #ced4da !important;
                    font-size: 0.875rem;
                    padding: 0.4375rem 0.75rem;
                }
                .custom-search-dropdown .dropdown-toggle:after {
                    display: none;
                }
                .custom-search-dropdown .dropdown-menu {
                    width: 100%;
                    max-height: 260px;
                    overflow-y: auto;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                    border: 1px solid #e3e6f0;
                    padding-top: 0;
                }
                .sticky-search-wrapper {
                    position: sticky;
                    top: 0;
                    background: white;
                    padding: 8px;
                    z-index: 5;
                    border-bottom: 1px solid #f1f3f9;
                }
                .custom-dropdown-item {
                    padding: 8px 16px;
                    font-size: 0.875rem;
                    color: #4e73df;
                    transition: background 0.15s ease;
                    cursor: pointer;
                }
                .custom-dropdown-item:hover {
                    background-color: #f8f9fc;
                    color: #2e59d9;
                }
                .filter-section {
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 20px;
                    margin-bottom: 20px;
                    border: 1px solid #e9ecef;
                }
                .month-picker {
                    display: ${activeFilters.filter === 'month' ? 'block' : 'none'};
                }
            `}</style>

            <Container fluid className="py-4">
                {/* Header Action Row */}
                <Row className="mb-4 align-items-center">
                    <Col>
                        <h2 className="text-secondary fw-bold mb-0">{translate("Reminders Dashboard")}</h2>
                        <p className="text-muted small mb-0">Monitor and track automated healthcare notifications</p>
                    </Col>
                    <Col xs="auto">
                        <Button variant="secondary" className="fw-semibold px-4 shadow-sm d-flex align-items-center gap-2" onClick={handleClear}>
                            <FaRotateLeft size={16} /> {translate("Reset All Filters")}
                        </Button>
                    </Col>
                </Row>

                {/* Stats Cards
                {stats && (
                    <Row className="mb-4 g-3">
                        <Col xl={3} sm={6}>
                            <Card className="stat-card-advanced border-start border-danger border-4 shadow-sm bg-white h-100">
                                <Card.Body className="d-flex align-items-center justify-content-between p-4">
                                    <div>
                                        <span className="text-muted text-uppercase tracking-wider small fw-bold d-block mb-1">
                                            Due Today
                                        </span>
                                        <h3 className="mb-0 fw-bold text-danger display-6">{stats.today}</h3>
                                    </div>
                                    <div className="icon-shape-wrapper bg-danger bg-opacity-10 text-danger" style={{ minWidth: '48px' }}>
                                        <FaBell size={22} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl={3} sm={6}>
                            <Card className="stat-card-advanced border-start border-warning border-4 shadow-sm bg-white h-100">
                                <Card.Body className="d-flex align-items-center justify-content-between p-4">
                                    <div>
                                        <span className="text-muted text-uppercase tracking-wider small fw-bold d-block mb-1">
                                            Next 3 Days
                                        </span>
                                        <h3 className="mb-0 fw-bold text-warning display-6">{stats.next_3_days}</h3>
                                    </div>
                                    <div className="icon-shape-wrapper bg-warning bg-opacity-10 text-warning" style={{ minWidth: '48px' }}>
                                        <FaCalendarDays size={22} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl={3} sm={6}>
                            <Card className="stat-card-advanced border-start border-info border-4 shadow-sm bg-white h-100">
                                <Card.Body className="d-flex align-items-center justify-content-between p-4">
                                    <div>
                                        <span className="text-muted text-uppercase tracking-wider small fw-bold d-block mb-1">
                                            Total Pending
                                        </span>
                                        <h3 className="mb-0 fw-bold text-info display-6">{stats.pending}</h3>
                                    </div>
                                    <div className="icon-shape-wrapper bg-info bg-opacity-10 text-info" style={{ minWidth: '48px' }}>
                                        <FaHourglassHalf size={22} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl={3} sm={6}>
                            <Card className="stat-card-advanced border-start border-success border-4 shadow-sm bg-white h-100">
                                <Card.Body className="d-flex align-items-center justify-content-between p-4">
                                    <div>
                                        <span className="text-muted text-uppercase tracking-wider small fw-bold d-block mb-1">
                                            Total Managed
                                        </span>
                                        <h3 className="mb-0 fw-bold text-success display-6">{stats.total}</h3>
                                    </div>
                                    <div className="icon-shape-wrapper bg-success bg-opacity-10 text-success" style={{ minWidth: '48px' }}>
                                        <FaFolderOpen size={22} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )} */}

                {/* Filter Section */}
                <div className="filter-section">
                    <Row className="align-items-center mb-3">
                        <Col>
                            <h5 className="fw-bold text-secondary mb-0 d-flex align-items-center gap-2">
                                <FaSliders className="text-primary" /> Filters
                            </h5>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        {/* Date Filter */}
                        <Col xl={3} lg={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-dark d-flex align-items-center gap-2 small mb-2">
                                    <FaClock className="text-muted" /> Date Filter
                                </Form.Label>
                                <Dropdown className="custom-search-dropdown">
                                    <Dropdown.Toggle id="dropdown-date-filter" size="sm" className="rounded-2">
                                        <span>{selectedDateLabel}</span>
                                        <FaChevronDown size={11} className="text-muted" />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {dateFilterOptions.map(option => (
                                            <div
                                                key={option.value}
                                                className="custom-dropdown-item"
                                                onClick={() => handleDateFilterChange(option.value)}
                                                style={{
                                                    backgroundColor: activeFilters.filter === option.value ? '#e7f1ff' : 'transparent',
                                                    fontWeight: activeFilters.filter === option.value ? 'bold' : 'normal'
                                                }}
                                            >
                                                {option.label}
                                            </div>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Form.Group>
                        </Col>

                        {/* Month Picker - Shows only when month filter is selected */}
                        {/* Month Picker - Always Visible */}
                        <Col xl={3} lg={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-dark d-flex align-items-center gap-2 small mb-2">
                                    <FaCalendar className="text-muted" /> Select Month
                                </Form.Label>
                                <Form.Control
                                    type="month"
                                    size="sm"
                                    value={activeFilters.month}
                                    onChange={handleMonthChange}
                                    className="rounded-2"
                                />
                            </Form.Group>
                        </Col>


                    </Row>

                    <hr className="my-3" />


                </div>

                {/* Main Data Render */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" className="my-3" />
                                <p className="text-muted small">{translate("Parsing and filtering records...")}</p>
                            </div>
                        ) : (
                            <RemindersTable reminders={reminders} AuthToken={Authtoken} />
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </PageLayout>
    );
};

export default ReminderPage;